import {useEffect, useState} from "react";
import {Col, Row, Collapse} from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import ActionBar from "~/layout/ActionBar";
import {Icon, Loading} from "~/components";
import {
	apiError,
	handleRequest,
	numberFormat
} from "~/utils";
import {reportApi, saleApi} from "~/api";
import {useGroup} from "~/hooks";
import {
	SaleStaffSummarySearch,
	SaleStaffSummaryTable,
	SaleStaffSummaryDetailModal
} from "../../components";

const cards = [
	{key: 'sources', label: 'Nguồn tìm được', color: '#16a34a', money: false},
	{key: 'calls', label: 'Cuộc gọi', color: '#2563eb', money: false},
	{key: 'appointments', label: 'Cuộc hẹn', color: '#0891b2', money: false},
	{key: 'consultations', label: 'Tư vấn hoàn thành', color: '#7c3aed', money: false},
	{key: 'contracts', label: 'Hợp đồng mới', color: '#ea580c', money: false},
	{key: 'revenue', label: 'Doanh thu', color: '#dc2626', money: true},
];

//Mặc định: từ ngày 1 đến ngày hiện tại của tháng hiện tại
const defaultTime = [dayjs().startOf('month').unix(), dayjs().endOf('day').unix()];

function SaleStaffSummaryReport() {

	const listGroup = useGroup();

	const [userOptions, setUserOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState({time: defaultTime, companyId: '', groupType: 'group', groupId: undefined, role: '', userId: undefined, userStatus: ''});
	const [data, setData] = useState({
		total: {sources: 0, calls: 0, appointments: 0, consultations: 0, cancels: 0, contracts: 0, revenue: 0},
		totalPrev: {sources: 0, calls: 0, appointments: 0, consultations: 0, cancels: 0, contracts: 0, revenue: 0},
		staff: [],
	});

	//So sánh tăng/giảm so với cùng kỳ tháng trước
	const deltaInfo = (cur, prev) => {
		cur = cur || 0;
		prev = prev || 0;
		if(prev === 0) {
			if(cur === 0) return {text: '—', color: '#9ca3af'};
			return {text: 'Mới', color: '#16a34a'};
		}
		const pct = Math.round(((cur - prev) / prev) * 100);
		if(pct === 0) return {text: '0%', color: '#9ca3af'};
		return pct > 0
			? {text: `▲ ${pct}%`, color: '#16a34a'}
			: {text: `▼ ${Math.abs(pct)}%`, color: '#dc2626'};
	};

	//Danh sách nhân viên trong phạm vi quyền (để lọc)
	useEffect(() => {
		(async () => {
			const [error, response] = await handleRequest(saleApi.callSourceTransferUsers());
			if(!error && response?.data) {
				setUserOptions(response.data.map((user) => ({
					value: user.id,
					label: `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.username,
				})));
			}
		})();
	}, []);

	//Load báo cáo
	useEffect(() => { handleLoad().then(); }, [filter]);

	const handleLoad = async () => {
		setLoading(true);
		const [error, response] = await handleRequest(reportApi.saleStaffSummary(filter));
		const message = apiError(`Lấy thông tin báo cáo thất bại`, error, response);
		if(!message) {
			setData(response.data);
		}
		setLoading(false);
	}

	const handleFilterChange = (key, value) => {
		setFilter((prev) => ({...prev, [key]: value, page: 1}));
	};

	//Modal chi tiết khi click vào ô số liệu
	const [detail, setDetail] = useState({open: false, userId: null, userName: '', type: '', typeLabel: ''});

	const handleCellClick = (d) => setDetail({...d, open: true});

	return (
		<>
			<ActionBar title={'Báo cáo tổng hợp đánh giá nhân viên kinh doanh'}></ActionBar>
			<div className="container d-block">
				<SaleStaffSummarySearch filter={filter} listGroup={listGroup} userOptions={userOptions} onChange={handleFilterChange} />
				<div className="content-scroll" style={{padding: '12px'}}>
					<Collapse
						className="mb-3"
						defaultActiveKey={['legend']}
						items={[{
							key: 'legend',
							label: 'Chú thích: báo cáo này thống kê những gì? (bấm để thu gọn)',
							children: (
								<div style={{fontSize: '13px', lineHeight: 1.9}}>
									<p>Tổng hợp hoạt động & kết quả của <b>nhân viên phòng kinh doanh</b> trong khoảng thời gian đã chọn (mặc định tháng hiện tại). Có thể lọc theo công ty, nhóm, chức vụ, trạng thái làm việc và từng nhân viên.</p>
									<ul style={{margin: 0, paddingLeft: '18px'}}>
										<li><b>Nguồn</b>: số nguồn (liên hệ) tìm được — theo ngày tạo.</li>
										<li><b>Cuộc gọi</b>: số lần gọi phát sinh trong kỳ — theo lịch sử gọi.</li>
										<li><b>Hẹn</b>: số lịch hẹn tư vấn được tạo — theo ngày tạo (người tạo hẹn).</li>
										<li><b>Tư vấn</b>: số buổi tư vấn <b>đã hoàn thành</b> — tính cho người tư vấn (assign).</li>
										<li><b>Hủy hẹn</b>: số lịch hẹn bị hủy.</li>
										<li><b>Hợp đồng</b>: số hợp đồng <b>mới</b> ký (loại mới / nâng cấp mới), <b>không tính tên miền</b> — theo ngày ký. <b>Host + web</b> cùng 1 khách hàng & cùng tháng ký tính là <b>1</b>; hợp đồng <b>web giá trị 0 không tính</b>. Tỉ số trong ngoặc là <b>tỉ lệ chốt</b> dạng "tư vấn:hợp đồng" — vd <b>4:1</b> nghĩa là 4 cuộc tư vấn được 1 hợp đồng.</li>
										<li><b>Doanh thu</b>: doanh thu thực thu đã duyệt, <b>không tính tên miền</b> — quy về nhân viên.</li>
										<li><b>Xem theo nhóm / nhánh</b>: "theo nhóm" chỉ lấy đúng nhóm đã chọn; "theo nhánh" lấy cả nhóm con và quản lý thuộc nhánh đó.</li>
										<li><b>So với kỳ trước</b> (dưới mỗi thẻ tổng): so sánh với <b>cùng khoảng thời gian của tháng liền trước</b>. <span className="color-green">▲ xanh</span> = tăng, <span className="color-red">▼ đỏ</span> = giảm; trong ngoặc là số liệu kỳ trước.</li>
										<li><b>% trong ngoặc</b>: tỷ lệ chuyển đổi so với bước liền trước. Ví dụ ở cột Hẹn, % = Hẹn ÷ Cuộc gọi.</li>
										<li><b>Dấu "—"</b>: chưa đủ mẫu để tính tỉ lệ (cần ≥ 5 với cuộc gọi/nguồn, ≥ 3 với hẹn/tư vấn) nên ẩn % để tránh tỉ lệ ảo. Ví dụ "Hẹn = 4 (—)" nghĩa là có 4 hẹn nhưng số cuộc gọi còn quá ít để tính tỉ lệ.</li>
										<li><b>DT TB/HĐ</b>: doanh thu trung bình mỗi hợp đồng = Doanh thu ÷ Số hợp đồng. Cho biết nhân viên bán được hợp đồng giá trị cao hay thấp.</li>
										<li><b>Dòng nhỏ ▲/▼% trong bảng</b> (dưới mỗi ô số): so sánh của <b>chính nhân viên đó</b> với cùng kỳ tháng trước — <span className="color-green">▲ xanh</span> = tăng, <span className="color-red">▼ đỏ</span> = giảm, "Mới" = kỳ trước chưa có. Rê chuột để xem số liệu kỳ trước.</li>
										<li>Bấm tiêu đề các cột số để <b>sắp xếp tăng/giảm</b>. Mặc định xếp theo doanh thu giảm dần.</li>
									</ul>
								</div>
							),
						}]}
					/>
					<div className="content-shadow pd-1 w-100 mb-3">
						<Row gutter={10}>
							{cards.map((card) => {
								const delta = deltaInfo(data.total[card.key], data.totalPrev?.[card.key]);
								return (
								<Col xs={12} sm={8} md={4} key={card.key}>
									<div className="mb-1" style={{padding: '10px 12px', borderRadius: '8px', background: '#f7f8fa', border: '1px solid #eef0f3'}}>
										<div style={{fontSize: '13px', color: '#6b7280', marginBottom: '4px'}}>{card.label}</div>
										<div style={{fontSize: '20px', fontWeight: 700, lineHeight: 1.2, color: card.color}}>
											{numberFormat(data.total[card.key])}{card.money ? ' đ' : ''}
										</div>
										<div style={{fontSize: '11px', marginTop: '4px', color: delta.color}}>
											{delta.text} <span style={{color: '#9ca3af'}}>so với kỳ trước ({numberFormat(data.totalPrev?.[card.key] || 0)}{card.money ? ' đ' : ''})</span>
										</div>
									</div>
								</Col>
								);
							})}
						</Row>
					</div>

					<div className="content-shadow pd-1 w-100">
						{loading && <Loading />}
						{(!loading && !_.isEmpty(data.staff)) && <SaleStaffSummaryTable staff={data.staff} total={data.total} onCellClick={handleCellClick} />}
						{(!loading && _.isEmpty(data.staff)) && (
							<div className="text-center pd-2 color-gray">
								<div style={{fontSize: '32px'}}>{Icon.search}</div>
								<p>Không có dữ liệu nhân viên kinh doanh trong khoảng thời gian này</p>
							</div>
						)}
					</div>
				</div>
			</div>
			<SaleStaffSummaryDetailModal
				open={detail.open}
				detail={detail}
				time={filter.time}
				onClose={() => setDetail((prev) => ({...prev, open: false}))}
			/>
		</>
	)
}

export default SaleStaffSummaryReport;
